/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Camera, User, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useVendorServices, useVendorPortfolioImages } from '../../services/vendorService';
import { useCheckFavorite, useAddFavorite, useRemoveFavorite } from '../../services/favoriteVendorService';
import { useToast, ToastContainer } from '../../components/shared/Toast';
import useShare from '../../hooks/useShare';
import VendorHeroGallery from '../../components/client/VendorHeroGallery';
import VendorHeaderInfo from '../../components/client/VendorHeaderInfo';
import VendorKeyDetails from '../../components/client/VendorKeyDetails';
import VendorHighlights from '../../components/client/VendorHighlights';
import VendorAmenities from '../../components/client/VendorAmenities';
import VendorBookingWidget from '../../components/client/VendorBookingWidget';
import VendorSocialMedia from '../../components/client/VendorSocialMedia';
import VendorReviews from '../../components/client/VendorReviews';
import ServiceCard from '../../components/client/ServiceCard';
import VendorPublicDiscountCodes from '../../components/client/VendorPublicDiscountCodes';
import VendorProfileSkeleton from '../../components/shared/VendorProfileSkeleton';
import VendorBrandBanner from '../../components/client/VendorBrandBanner';
import VendorServiceAreas from '../../components/client/VendorServiceAreas';
import { buildBrandCssVars, getBrandFromVendor, getBrandButtonStyle, getBrandCtaLabel, isBrandActive, filterBrandAssetsFromGallery } from '../../utils/vendorBranding';

const ClientVendorProfile = () => {
  const { id } = useParams();
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [showMobileBooking, setShowMobileBooking] = useState(false);
  
  // Fetch vendor services data from API
  const { data: servicesResponse, isLoading, error } = useVendorServices(id);
  
  // Transform API data to get vendor ID
  const services = servicesResponse?.data || [];
  const vendor = services[0]?.vendor_profile;
  const vendorId = vendor?.id || id;
  
  // Check if vendor is favorite (only when authenticated and vendorId is available)
  const { data: favoriteData, isLoading: checkingFavorite } = useCheckFavorite(vendorId, {
    enabled: !!vendorId && isAuthenticated
  });
  const isFavorited = favoriteData?.is_favorite || false;
  const favoriteId = favoriteData?.favorite_id || null;
  
  // Mutations for adding/removing favorites
  const addFavoriteMutation = useAddFavorite({
    onSuccess: (data) => {
      // Optimistically update the favorite status immediately
      // The query will refetch automatically, but we can update UI immediately
      toast.success('Added to Favorites', 'Vendor has been added to your favorites!');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to add to favorites';
      toast.error('Error', errorMessage);
    }
  });
  
  const removeFavoriteMutation = useRemoveFavorite({
    onSuccess: () => {
      // Optimistically update the favorite status immediately
      toast.success('Removed from Favorites', 'Vendor has been removed from your favorites.');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to remove from favorites';
      toast.error('Error', errorMessage);
    }
  });
  
  // Handle favorite toggle
  const handleToggleFavorite = () => {
    if (!isAuthenticated) {
      return;
    }
    
    if (isFavorited) {
      // Always try to remove, even if favoriteId is 'optimistic' - the mutation will handle it
      removeFavoriteMutation.mutate({ favoriteId, vendorId });
    } else if (vendorId) {
      addFavoriteMutation.mutate({ vendorId: vendorId });
    }
  };
  
  const isFavoriteLoading = addFavoriteMutation.isPending || removeFavoriteMutation.isPending || checkingFavorite;
  
  // Fetch vendor portfolio images
  const { data: portfolioImagesResponse, isLoading: portfolioLoading } = useVendorPortfolioImages(id);

  // Transform API data (vendor already defined above)
  const vendorUser = vendor?.user;
  const portfolioImages = portfolioImagesResponse?.data || [];
  const brand = getBrandFromVendor(vendor);
  const brandActive = isBrandActive(brand);
  const brandCssVars = buildBrandCssVars(brand);
  const mobileBtnStyle = getBrandButtonStyle(brand, 'primary');
  const mobileCtaLabel = getBrandCtaLabel(brand, 'View Services');
  const bannerMode = brandActive && brand.hero_style === 'banner';
  const headerLogoUrl = brandActive && brand.logo_url ? brand.logo_url : null;
  
  // Get unique categories
  const categories = [...new Map(
    services
      .map(service => service.service_category)
      .filter(Boolean)
      .map(cat => [cat.id, cat])
  ).values()];
  
  // Build portfolio image gallery - only portfolio images (image_type='portfolio')
  const portfolioGalleryImages = [];
  
  // Add portfolio images only (from user_images with image_type='portfolio')
  portfolioImages.forEach(image => {
    if (image.image_url) {
      portfolioGalleryImages.push(image.image_url);
    }
  });

  const displayGalleryImages = filterBrandAssetsFromGallery(portfolioGalleryImages, brand);

  // Calculate minimum price
  const minPrice = services.length > 0 
    ? Math.min(...services.map(s => parseFloat(s.base_price) || Infinity).filter(p => p > 0 && p !== Infinity))
    : null;

  // Share functionality hook
  const { handleShare } = useShare({
    title: vendor?.business_name || 'Vendor',
    text: `Check out ${vendor?.business_name} on PhotoBooth`,
  });

  if (isLoading || portfolioLoading) {
    return <VendorProfileSkeleton />;
  }

  if (error || !services.length) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-m-canvas-light px-4">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-m-border-light bg-m-surface">
            <Camera className="h-7 w-7 text-m-ink-muted" />
          </div>
          <h2 className="mb-3 text-2xl font-semibold tracking-tight text-m-ink">
            Vendor not found
          </h2>
          <p className="marketing-body mb-8 text-m-ink-muted">
            This vendor doesn’t exist or has no services available.
          </p>
          <Link
            to="/browse-vendors"
            className="marketing-btn marketing-btn-ink inline-flex gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Browse vendors
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer 
        toasts={toast.toasts} 
        onRemove={toast.removeToast}
        position="top-right"
      />
      <div className="min-h-screen bg-m-canvas-light" style={brandCssVars}>
      <div className="mx-auto max-w-7xl px-3 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8 lg:px-8">
        <div className="mb-4 sm:mb-5 lg:hidden">
          <Link
            to="/browse-vendors"
            className="inline-flex items-center gap-2 text-sm font-medium text-m-ink-muted transition-colors hover:text-m-ink"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </div>

        {/* Brand banner (optional) */}
        {bannerMode && (
          <VendorBrandBanner
            brand={brand}
            businessName={vendor?.business_name}
            logoUrl={headerLogoUrl}
          />
        )}

        {/* Header Info - At Top */}
        <div className="mb-4 sm:mb-6">
          <VendorHeaderInfo
            vendor={vendor}
            brand={brand}
            hideTitle={bannerMode}
            isFavorited={isFavorited}
            onToggleFavorite={handleToggleFavorite}
            onShare={handleShare}
            isLoading={isFavoriteLoading}
            isAuthenticated={isAuthenticated}
          />
              </div>

        {/* Hero Gallery - Portfolio Images Only */}
        <div className="mb-4 sm:mb-6 md:mb-8 -mx-3 sm:mx-0">
          <VendorHeroGallery 
            images={displayGalleryImages.filter(Boolean)} 
            vendorName={vendor?.business_name}
            onShowAll={() => setShowAllPhotos(true)}
          />
          </div>
                    
        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8 lg:gap-12">
          {/* Left Column - Main Content */}
          <div className="space-y-8">

            {/* Key Details Bar - Rating and Location */}
            <VendorKeyDetails vendor={vendor} servicesCount={services.length} />

            {/* Vendor Host Info */}
            {vendorUser && (
              <div className="flex items-center gap-4 border-b border-m-border-light pb-6">
                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full sm:h-16 sm:w-16 ${brandActive ? '' : 'bg-m-ink'}`}
                  style={brandActive ? { backgroundColor: brand.primary_color } : undefined}
                >
                  {vendorUser.profile_image_url ? (
                    <img
                      src={vendorUser.profile_image_url}
                      alt={vendorUser.name || 'Vendor'}
                      className="h-full w-full rounded-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <User className="h-6 w-6 text-white sm:h-8 sm:w-8" />
                  )}
                </div>
                <div>
                  <p className="text-base font-semibold tracking-tight text-m-ink sm:text-lg">
                    by {vendorUser.name || vendor?.business_name}
                  </p>
                  {vendor?.years_experience ? (
                    <p className="text-sm text-m-ink-muted">
                      {vendor.is_verified ? 'Verified vendor' : 'Vendor'} ·{' '}
                      {vendor.years_experience}+ years hosting
                    </p>
                  ) : null}
                </div>
              </div>
            )}

            {vendor?.description ? (
              <div className="border-b border-m-border-light pb-6">
                <p className="marketing-body whitespace-pre-line text-m-ink-muted">
                  {vendor.description}
                </p>
              </div>
            ) : null}

            {vendor?.social_media ? (
              <div className="border-b border-m-border-light pb-6">
                <VendorSocialMedia socialMedia={vendor.social_media} />
              </div>
            ) : null}

            <div className="border-b border-m-border-light pb-6">
              <VendorHighlights vendor={vendor} services={services} />
            </div>

            {vendor?.service_areas?.length > 0 && (
              <VendorServiceAreas areas={vendor.service_areas} />
            )}

            <VendorPublicDiscountCodes vendorId={vendorId} toast={toast} />

            {/* What this place offers */}
            <div className="border-b border-m-border-light pb-6">
              <h2 className="marketing-display-md mb-6 text-m-ink">
                What this vendor offers
              </h2>
              <VendorAmenities services={services} categories={categories} />
            </div>

            {/* Services Section */}
            <div>
              <h2 className="marketing-display-md mb-6 text-m-ink">Services</h2>

              {services.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-1 xl:grid-cols-2">
                  {services.map((service) => {
                    const category = service.service_category;
                    return (
                      <ServiceCard
                        key={service.id}
                        service={service}
                        vendorRating={vendor?.average_rating}
                        category={category}
                        brand={brand}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-2xl border border-m-border-light bg-m-surface py-12 text-center">
                  <Camera className="mx-auto mb-4 h-10 w-10 text-m-ink-faint" />
                  <h3 className="mb-2 text-lg font-medium tracking-tight text-m-ink">
                    No services found
                  </h3>
                  <p className="text-sm text-m-ink-muted">
                    No services available at the moment.
                  </p>
                </div>
              )}
            </div>

            {/* Reviews Section */}
            <div className="border-b border-m-border-light pb-6">
              <VendorReviews vendorId={id} />
            </div>
          </div>

          {/* Right Column - Sticky Booking Widget (Desktop Only) */}
          <div className="hidden lg:block">
            <div className="sticky top-20">
              <VendorBookingWidget
                vendor={vendor}
                services={services}
                minPrice={minPrice}
                isAuthenticated={isAuthenticated}
                brand={brand}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Booking Button - Fixed Bottom */}
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-m-border-light bg-m-surface/95 shadow-[0_-8px_30px_rgba(0,0,0,0.06)] backdrop-blur-sm lg:hidden">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-medium tracking-tight text-m-ink">
                  ${minPrice ? minPrice.toFixed(2) : 'Contact'}
                </span>
                {services.length > 0 && minPrice ? (
                  <span className="text-sm text-m-ink-muted">starting from</span>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowMobileBooking(true)}
              className={`marketing-btn shrink-0 text-sm ${
                brandActive ? 'text-white' : 'marketing-btn-ink'
              }`}
              style={brandActive ? mobileBtnStyle : undefined}
            >
              {mobileCtaLabel}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Booking Modal */}
      {showMobileBooking && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMobileBooking(false)}
          />

          <div className="absolute bottom-0 left-0 right-0 max-h-[90vh] overflow-y-auto rounded-t-2xl bg-m-surface shadow-xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-m-border-light bg-m-surface px-4 py-3">
              <h3 className="text-lg font-medium tracking-tight text-m-ink">
                Booking details
              </h3>
              <button
                type="button"
                onClick={() => setShowMobileBooking(false)}
                className="rounded-full p-2 transition-colors hover:bg-m-canvas-light"
              >
                <X className="h-5 w-5 text-m-ink-muted" />
              </button>
            </div>
            
            <div className="p-4">
              <VendorBookingWidget
                vendor={vendor}
                services={services}
                minPrice={minPrice}
                isAuthenticated={isAuthenticated}
                brand={brand}
              />
            </div>
          </div>
        </div>
      )}

      {/* Full Photo Gallery Modal */}
      {showAllPhotos && (
        <div 
          className="fixed inset-0 bg-black z-[99999] overflow-y-auto"
          onClick={() => setShowAllPhotos(false)}
        >
          <div className="max-w-7xl mx-auto p-4 sm:p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">
                {displayGalleryImages.length} photos
              </h2>
              <button
                onClick={() => setShowAllPhotos(false)}
                className="text-white hover:opacity-70 transition-opacity"
              >
                <span className="text-2xl">×</span>
              </button>
                            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayGalleryImages.map((image, index) => (
                <div key={index} className="relative aspect-video animate-pulse overflow-hidden rounded-lg bg-m-border-light">
                  <img
                    src={image}
                    alt={`${vendor?.business_name} - Photo ${index + 1}`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onLoad={(e) => {
                      e.target.parentElement.classList.remove('animate-pulse', 'bg-m-border-light');
                    }}
                    onError={(e) => {
                      e.target.parentElement.classList.remove('animate-pulse');
                      e.target.parentElement.classList.add('bg-m-canvas-light');
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default ClientVendorProfile;
